import { useCallback, useEffect, useMemo, useState } from "react";
import { getErrorDetails } from "../../../app/appUtils";
import BulkUploadModal from "../../../components/ui/BulkUploadModal";
import SuccessView from "../../../components/ui/SuccessView";
import { BulkUploadField } from "../../../lib/bulkUpload";
import { CrewMember, Driver } from "../../driver/@types";
import {
  useGetCrewMembersMutation,
  useGetDriversMutation,
} from "../../driver/driverApiSlice";
import { Vehicle } from "../../vehicle/@types";
import { useGetVehiclesMutation } from "../../vehicle/vehicleApiSlice";
import { CreateDispatchRequest } from "../@types";
import { useCreateDispatchMutation } from "../dispatchApiSlice";

const DISPATCH_BULK_FIELDS: BulkUploadField[] = [
  {
    key: "fromLocation",
    label: "Start location",
    required: true,
    defaultColumnName: "Start Location",
  },
  {
    key: "toLocation",
    label: "End location",
    required: true,
    defaultColumnName: "End Location",
  },
  {
    key: "approximateDistance",
    label: "Expected distance (KM)",
    required: true,
    type: "number",
    defaultColumnName: "Expected Distance covered (KM)",
  },
  {
    key: "driverName",
    label: "Driver",
    required: true,
    defaultColumnName: "Driver",
    hint: "Must match an existing driver name.",
  },
  {
    key: "crewMembers",
    label: "Crew members",
    required: false,
    defaultColumnName: "Crew Members",
    hint: "Separate multiple names with commas.",
  },
  {
    key: "fuelQuantity",
    label: "Fuel quantity",
    required: false,
    type: "number",
    defaultColumnName: "Fuel quantities",
  },
  {
    key: "fuelPricePerUnit",
    label: "Price per unit",
    required: false,
    type: "number",
    defaultColumnName: "Price per Unit",
  },
  {
    key: "fuelAmount",
    label: "Fuel amount",
    required: false,
    type: "number",
    defaultColumnName: "Amount",
    hint: "First Amount column in the sample sheet.",
  },
  {
    key: "fuelType",
    label: "Fuel type",
    required: false,
    defaultColumnName: "Fuel type",
    hint: "Petrol or Diesel.",
    example: "DISEAL, Diesel",
  },
  {
    key: "fuelDate",
    label: "Fuel date",
    required: false,
    type: "date",
    defaultColumnName: "Fuel Date",
  },
  {
    key: "odometerReading",
    label: "Odometer reading",
    required: false,
    type: "number",
    defaultColumnName: "Odometer Reading",
  },
  {
    key: "stationName",
    label: "Station name",
    required: false,
    defaultColumnName: "Station name",
  },
  {
    key: "otherExpenseDescription",
    label: "Other expense",
    required: false,
    defaultColumnName: "Other expenses",
    example: "TIRE",
  },
  {
    key: "otherExpenseAmount",
    label: "Other expense amount",
    required: false,
    type: "number",
    defaultColumnName: "Amount (2)",
    hint: "Second Amount column. Duplicate headers become Amount (2).",
  },
  {
    key: "totalIncomeEarned",
    label: "Total income",
    required: true,
    type: "number",
    defaultColumnName: "Total Income",
  },
  {
    key: "totalExpectedIncome",
    label: "Total expected income",
    required: true,
    type: "number",
    defaultColumnName: "Total expected income",
  },
  {
    key: "totalPassengers",
    label: "Total passengers",
    required: true,
    type: "number",
    defaultColumnName: "Total passengers",
  },
  {
    key: "perPassengerRate",
    label: "Rate per passenger",
    required: true,
    type: "number",
    defaultColumnName: "Rate per passengers",
  },
  {
    key: "incomeDescription",
    label: "Income description",
    required: false,
    defaultColumnName: "Description 1",
  },
  {
    key: "incomeExpectedAmount",
    label: "Income expected amount",
    required: false,
    type: "number",
    defaultColumnName: "Amount 1",
  },
  {
    key: "incomeEarnedAmount",
    label: "Income earned amount",
    required: false,
    type: "number",
    defaultColumnName: "Amount earned 1",
  },
  {
    key: "busNumber",
    label: "Bus number",
    required: false,
    defaultColumnName: "Bus Number",
    hint: "License plate. If blank, the current vehicle is used.",
  },
];

const ABSENT_VALUES = new Set([
  "",
  "none",
  "null",
  "n/a",
  "na",
  "-",
  "--",
  "nothing",
  "empty",
]);

interface DispatchBulkUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehicleId: string;
  vehiclePlate?: string;
}

const asList = <T,>(value: unknown): T[] => {
  if (Array.isArray(value)) return value as T[];
  if (
    value &&
    typeof value === "object" &&
    Array.isArray((value as { data?: unknown }).data)
  ) {
    return (value as { data: T[] }).data;
  }
  return [];
};

const normalize = (value: string) =>
  value.replace(/\s+/g, " ").trim().toLowerCase();

const normalizePlate = (value: string) =>
  value.replace(/[\s-]/g, "").toLowerCase();

const stripDecorators = (value: string) =>
  normalize(value.replace(/\(.*?\)/g, " ").replace(/\[.*?\]/g, " "));

const isAbsent = (value: unknown): boolean => {
  if (value == null) return true;
  return ABSENT_VALUES.has(normalize(String(value)));
};

const namesMatch = (candidate: string, needle: string): boolean => {
  const a = normalize(candidate);
  const b = normalize(needle);
  if (!a || !b) return false;
  if (a === b) return true;
  const strippedA = stripDecorators(candidate);
  const strippedB = stripDecorators(needle);
  if (strippedA && strippedA === strippedB) return true;
  if (strippedA.length < 3 || strippedB.length < 3) return false;
  return strippedA.includes(strippedB) || strippedB.includes(strippedA);
};

const searchKeyword = (value: string): string => {
  const stripped = stripDecorators(value);
  return stripped.split(" ").filter(Boolean)[0] || normalize(value);
};

const resolveFuelType = (value: string): string => {
  const needle = normalize(value).replace(/[^a-z]/g, "");
  if (needle.startsWith("die") || needle === "diseal" || needle === "dsl") {
    return "Diesel";
  }
  if (needle.startsWith("pet") || needle === "pms") {
    return "Petrol";
  }
  return value.trim();
};

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const DispatchBulkUploadModal: React.FC<DispatchBulkUploadModalProps> = ({
  isOpen,
  onClose,
  vehicleId,
  vehiclePlate,
}) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);

  const [getDrivers, { data: driversResponse, isLoading: isDriversLoading }] =
    useGetDriversMutation();
  const [searchDrivers] = useGetDriversMutation();
  const [getCrewMembers, { data: crewResponse, isLoading: isCrewLoading }] =
    useGetCrewMembersMutation();
  const [searchCrewMembers] = useGetCrewMembersMutation();
  const [getVehicles, { data: vehiclesResponse, isLoading: isVehiclesLoading }] =
    useGetVehiclesMutation();
  const [searchVehicles] = useGetVehiclesMutation();
  const [createDispatch] = useCreateDispatchMutation();

  useEffect(() => {
    if (!isOpen) return;
    getDrivers({ pageNumber: 0, pageSize: 1000, orderBy: ["CreatedOn"] });
    getCrewMembers({ pageNumber: 0, pageSize: 1000 });
    getVehicles({ pageNumber: 0, pageSize: 1000 });
  }, [isOpen, getDrivers, getCrewMembers, getVehicles]);

  useEffect(() => {
    if (!isOpen) {
      setShowSuccess(false);
      setUploadedCount(0);
    }
  }, [isOpen]);

  const drivers = useMemo(
    () => asList<Driver>(driversResponse?.data?.data ?? driversResponse?.data),
    [driversResponse?.data],
  );
  const crewMembers = useMemo(
    () => asList<CrewMember>(crewResponse?.data?.data ?? crewResponse?.data),
    [crewResponse?.data],
  );
  const vehicles = useMemo(
    () =>
      asList<Vehicle>(vehiclesResponse?.data?.data ?? vehiclesResponse?.data),
    [vehiclesResponse?.data],
  );

  const resolveDriver = useCallback(
    (value: string, list = drivers) => {
      const needle = String(value ?? "").trim();
      if (!needle) return undefined;
      return list.find((driver) => namesMatch(driver.fullName, needle));
    },
    [drivers],
  );

  const resolveCrew = useCallback(
    (value: string, list = crewMembers) => {
      const needle = String(value ?? "").trim();
      if (!needle) return undefined;
      return list.find(
        (crew) =>
          namesMatch(crew.fullName, needle) ||
          namesMatch(crew.department || "", needle),
      );
    },
    [crewMembers],
  );

  const resolveVehicle = useCallback(
    (value: string, list = vehicles) => {
      const needle = normalizePlate(value);
      if (!needle) return undefined;
      return list.find((vehicle) => {
        const plates = [
          vehicle.licensePlate,
          vehicle.vehicleName,
          vehicle.vehicleFormatedId,
          vehicle.chassisNumber,
        ]
          .filter(Boolean)
          .map((item) => normalizePlate(String(item)));
        return plates.includes(needle);
      });
    },
    [vehicles],
  );

  const findDriver = useCallback(
    async (value: string) => {
      const local = resolveDriver(value);
      if (local) return local;
      const keyword = searchKeyword(value);
      if (!keyword) return undefined;
      const response = await searchDrivers({
        pageNumber: 0,
        pageSize: 50,
        keyword,
        orderBy: ["CreatedOn"],
      }).unwrap();
      return resolveDriver(
        value,
        asList<Driver>(response?.data?.data ?? response?.data),
      );
    },
    [resolveDriver, searchDrivers],
  );

  const findCrew = useCallback(
    async (value: string) => {
      const local = resolveCrew(value);
      if (local) return local;
      const keyword = searchKeyword(value);
      if (!keyword) return undefined;
      const response = await searchCrewMembers({
        pageNumber: 0,
        pageSize: 50,
        keyword,
      }).unwrap();
      return resolveCrew(
        value,
        asList<CrewMember>(response?.data?.data ?? response?.data),
      );
    },
    [resolveCrew, searchCrewMembers],
  );

  const findVehicle = useCallback(
    async (value: string) => {
      const local = resolveVehicle(value);
      if (local) return local;
      const keyword = String(value ?? "").trim();
      if (!keyword) return undefined;
      const response = await searchVehicles({
        pageNumber: 0,
        pageSize: 50,
        keyword,
      }).unwrap();
      return resolveVehicle(
        value,
        asList<Vehicle>(response?.data?.data ?? response?.data),
      );
    },
    [resolveVehicle, searchVehicles],
  );

  const validateRecord = useCallback(
    (record: Record<string, unknown>) => {
      const errors: string[] = [];
      const driverName = String(record.driverName ?? "");
      if (!isDriversLoading && driverName && !resolveDriver(driverName)) {
        errors.push(`Driver "${driverName}" was not found`);
      }

      const busNumber = String(record.busNumber ?? "").trim();
      if (!isAbsent(busNumber) && !isVehiclesLoading && !resolveVehicle(busNumber)) {
        errors.push(`Vehicle "${busNumber}" was not found`);
      }

      const crewValue = String(record.crewMembers ?? "");
      if (!isCrewLoading && !isAbsent(crewValue)) {
        const missing = crewValue
          .split(/[,;/|]/)
          .map((item) => item.trim())
          .filter(Boolean)
          .filter((name) => !resolveCrew(name));
        if (missing.length > 0) {
          errors.push(`Crew member "${missing.join(", ")}" was not found`);
        }
      }

      const fuelType = String(record.fuelType ?? "").trim();
      if (fuelType) {
        const resolved = resolveFuelType(fuelType);
        if (!["Petrol", "Diesel"].includes(resolved)) {
          errors.push("Fuel type must be Petrol or Diesel");
        }
      }

      return errors;
    },
    [
      isCrewLoading,
      isDriversLoading,
      isVehiclesLoading,
      resolveCrew,
      resolveDriver,
      resolveVehicle,
    ],
  );

  const handleUploadRow = async (row: Record<string, unknown>) => {
    const driver = await findDriver(String(row.driverName ?? ""));
    if (!driver?.id) {
      throw new Error(`Driver "${row.driverName}" was not found`);
    }

    const busNumber = String(row.busNumber ?? "").trim();
    const vehicle = isAbsent(busNumber)
      ? { id: vehicleId }
      : await findVehicle(busNumber);
    if (!vehicle?.id) {
      throw new Error(
        busNumber
          ? `Vehicle "${busNumber}" was not found`
          : "No vehicle was selected for this dispatch",
      );
    }

    const crewNames = String(row.crewMembers ?? "")
      .split(/[,;/|]/)
      .map((item) => item.trim())
      .filter((name) => name && !isAbsent(name));

    const crews = [];
    for (const name of crewNames) {
      const crew = await findCrew(name);
      if (!crew) {
        throw new Error(`Crew member "${name}" was not found`);
      }
      crews.push({
        id: crew.id,
        name: crew.fullName,
        crewType: crew.department || "Crew",
      });
    }

    const hasFuel =
      toNumber(row.fuelQuantity) > 0 ||
      toNumber(row.fuelPricePerUnit) > 0 ||
      toNumber(row.fuelAmount) > 0 ||
      !isAbsent(row.fuelType) ||
      !isAbsent(row.stationName) ||
      toNumber(row.odometerReading) > 0;

    const fuelType = hasFuel
      ? resolveFuelType(String(row.fuelType || ""))
      : "";
    if (hasFuel && !["Petrol", "Diesel"].includes(fuelType)) {
      throw new Error("Fuel type must be Petrol or Diesel");
    }

    const expenses =
      !isAbsent(row.otherExpenseDescription) || toNumber(row.otherExpenseAmount)
        ? [
            {
              description: String(
                row.otherExpenseDescription || "Other expense",
              ),
              amount: toNumber(row.otherExpenseAmount),
            },
          ]
        : undefined;

    const incomes =
      !isAbsent(row.incomeDescription) ||
      toNumber(row.incomeExpectedAmount) ||
      toNumber(row.incomeEarnedAmount)
        ? [
            {
              description: String(row.incomeDescription || "Trip income"),
              expectedAmount: toNumber(row.incomeExpectedAmount),
              earnedAmount: toNumber(row.incomeEarnedAmount),
            },
          ]
        : undefined;

    const payload: CreateDispatchRequest = {
      fromLocation: String(row.fromLocation || ""),
      toLocation: String(row.toLocation || ""),
      driverId: driver.id,
      vehicleId: vehicle.id,
      approximateDistance: toNumber(row.approximateDistance),
      totalExpectedIncome: toNumber(row.totalExpectedIncome),
      totalIncomeEarned: toNumber(row.totalIncomeEarned),
      totalPassengers: toNumber(row.totalPassengers),
      perPassengerRate: toNumber(row.perPassengerRate),
      crews: crews.length > 0 ? crews : undefined,
      expenses,
      incomes,
    };

    if (hasFuel) {
      payload.fuelExpense = {
        quantity: toNumber(row.fuelQuantity),
        pricePerUnit: toNumber(row.fuelPricePerUnit),
        amount: toNumber(row.fuelAmount),
        fuelType,
        fuelDate:
          String(row.fuelDate || "") || new Date().toISOString().split("T")[0],
        odometerReading: toNumber(row.odometerReading),
        stationName: String(row.stationName || ""),
      };
    }

    try {
      await createDispatch(payload).unwrap();
    } catch (error) {
      const message =
        getErrorDetails(error).message || "Failed to create this dispatch.";
      throw new Error(message);
    }
  };

  const handleUploadComplete = (result: {
    succeeded: number;
    failed: number;
  }) => {
    if (result.failed === 0 && result.succeeded > 0) {
      setUploadedCount(result.succeeded);
      setShowSuccess(true);
    }
  };

  const handleClose = () => {
    setShowSuccess(false);
    setUploadedCount(0);
    onClose();
  };

  if (showSuccess) {
    return (
      <BulkUploadSuccess
        isOpen={isOpen}
        count={uploadedCount}
        onClose={handleClose}
      />
    );
  }

  return (
    <BulkUploadModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Bulk upload dispatches"
      entityLabel="dispatches"
      entitySingular="dispatch"
      fields={DISPATCH_BULK_FIELDS}
      storageKey="dispatches"
      templateFileName="dispatch-bulk-upload-template"
      onUploadRow={handleUploadRow}
      onUploadComplete={handleUploadComplete}
      getRowLabel={(row) =>
        [
          row.busNumber || vehiclePlate,
          row.driverName,
          [row.fromLocation, row.toLocation].filter(Boolean).join(" → "),
        ]
          .filter(Boolean)
          .join(" · ")
      }
      validateRecord={validateRecord}
    />
  );
};

const BulkUploadSuccess: React.FC<{
  isOpen: boolean;
  count: number;
  onClose: () => void;
}> = ({ isOpen, count, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed w-full h-full inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-lg shadow-xl max-w-full sm:max-w-[80vw] lg:max-w-[45vw] w-full mx-4 overflow-hidden">
        <SuccessView
          handleCloseModal={onClose}
          heading=""
          message={`${count} ${
            count === 1 ? "dispatch has" : "dispatches have"
          } been uploaded successfully.`}
        />
      </div>
    </div>
  );
};

export default DispatchBulkUploadModal;
